import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Bug,
  Check,
  CheckCheck,
  CheckCircle2,
  Clock3,
  CreditCard,
  HelpCircle,
  LifeBuoy,
  MessagesSquare,
  MoreHorizontal,
  PlayCircle,
  Plus,
  SendHorizontal,
  ShieldCheck,
  Video,
  Wrench,
  X,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import env from "../../config";
import { cx } from "../../utils";
import Input, { Label, Textarea } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";

/**
 * Support for this app: open a ticket, then talk it through with the team.
 *
 * One inbox — tickets on the left, the open conversation on the right with the
 * composer pinned to the bottom. Tickets and their conversations are ours
 * (backend/src/routes/support), so anyone in the account can open one. Replies
 * arrive live over Server-Sent Events; sending is an ordinary request, shown
 * at once and confirmed when the server has it.
 */

// The same limits the backend enforces.
const SUBJECT_MIN = 3;
const SUBJECT_MAX = 150;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;

const CATEGORIES = [
  { id: "Something isn't working", label: "Something's broken", icon: Bug },
  { id: "Setup help", label: "Setup help", icon: Wrench },
  { id: "Question", label: "Question", icon: HelpCircle },
  { id: "Billing", label: "Billing", icon: CreditCard },
  { id: "Other", label: "Other", icon: MoreHorizontal },
];

// A Loom share or embed link — the backend checks it the same way.
const LOOM = /^https?:\/\/(?:www\.)?loom\.com\/(?:share|embed)\/(?:[\w-]*-)?[0-9a-f]{32}(?:[/?#].*)?$/i;

// Worded for the customer: "pending" means we're waiting on them.
const STATUS = {
  open: { label: "Open", dot: "bg-brand-500", pill: "bg-brand-50 text-brand-700 ring-brand-200/70" },
  pending: { label: "Awaiting your reply", short: "Your reply", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 ring-amber-200/80" },
  resolved: { label: "Resolved", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 ring-emerald-200/80" },
  closed: { label: "Closed", dot: "bg-ink-400", pill: "bg-ink-100 text-ink-600 ring-ink-200/80" },
};

const StatusPill = ({ status, short }) => {
  const s = STATUS[status] || STATUS.open;
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", s.pill)}>
      <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} />
      {short ? s.short || s.label : s.label}
    </span>
  );
};

/* ------------------------------------------------------------------ time */

const sameDay = (a, b) => a.toDateString() === b.toDateString();

const ago = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return d.toLocaleDateString(undefined, { weekday: "short" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const clock = (value) => new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const dayLabel = (value) => {
  const d = new Date(value);
  if (sameDay(d, new Date())) return "Today";
  if (sameDay(d, new Date(Date.now() - 86400000))) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
};

const upsert = (list, ticket) =>
  [ticket, ...list.filter((t) => t._id !== ticket._id)].sort(
    (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt),
  );

// Day dividers; consecutive messages from the same side within five minutes
// run together under one name.
const groupMessages = (messages) => {
  const out = [];
  messages.forEach((m, i) => {
    const prev = messages[i - 1];
    const d = new Date(m.createdAt);
    if (!prev || !sameDay(new Date(prev.createdAt), d)) out.push({ kind: "day", key: `d-${m._id}`, label: dayLabel(m.createdAt) });
    const last = out[out.length - 1];
    const joins =
      last?.kind === "group" &&
      last.from === m.from &&
      last.authorName === m.authorName &&
      d - new Date(last.messages[last.messages.length - 1].createdAt) < 5 * 60 * 1000;
    if (joins) last.messages.push(m);
    else out.push({ kind: "group", key: `g-${m._id}`, from: m.from, authorName: m.authorName, messages: [m] });
  });
  return out;
};

const TeamAvatar = ({ size = "h-8 w-8" }) => (
  <span className={cx("grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-sm", size)} aria-hidden>
    <LifeBuoy size={14} />
  </span>
);

/* ------------------------------------------------------------- composer */

const Composer = ({ onSend, closed }) => {
  const [draft, setDraft] = useState("");
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    setDraft(""); // shown at once; a failure puts the text back
    onSend(body, () => setDraft(body));
    ref.current?.focus();
  };

  const ready = draft.trim().length > 0;

  return (
    <div className="border-t border-ink-150 bg-white px-4 py-3.5">
      <div className="rounded-2xl border border-ink-200 bg-white shadow-sm transition-all focus-within:border-brand-300 focus-within:shadow-glow">
        <textarea
          ref={ref}
          rows={1}
          value={draft}
          maxLength={MESSAGE_MAX}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={closed ? "Write a message to reopen this ticket…" : "Write a reply…"}
          aria-label="Message"
          className="block max-h-40 min-h-[2.75rem] w-full resize-none rounded-t-2xl border-0 bg-transparent px-4 pb-1 pt-3 text-[14px] leading-6 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-0"
        />
        <div className="flex items-center justify-between gap-3 px-3 pb-2.5 pt-1">
          <p className="flex items-center gap-1.5 pl-1 text-[11.5px] text-ink-400">
            <ShieldCheck size={12} aria-hidden /> Never share passwords or API keys
          </p>
          <button
            type="button"
            onClick={send}
            disabled={!ready}
            className={cx(
              "inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-[13px] font-semibold transition-all",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
              ready ? "bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow hover:brightness-110" : "bg-ink-100 text-ink-400",
            )}
          >
            Send <SendHorizontal size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- thread */

const Thread = ({ ticket, messages, loading, onSend, onBack }) => {
  const endRef = useRef(null);
  const groups = useMemo(() => groupMessages(messages), [messages]);
  const lastGroup = [...groups].reverse().find((g) => g.kind === "group");

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages.length, ticket._id]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-ink-150 px-5 py-4">
        <button
          type="button"
          onClick={onBack}
          className="-ml-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700 md:hidden"
          aria-label="Back to tickets"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15.5px] font-semibold tracking-tight text-ink-900">{ticket.subject}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-ink-500">
            <span className="rounded-md bg-ink-100/80 px-1.5 py-0.5 font-mono tabular-nums text-ink-600">#{ticket.number}</span>
            <span className="rounded-md bg-ink-100/80 px-1.5 py-0.5 font-medium text-ink-600">{ticket.category}</span>
            {ticket.loomUrl && (
              <a
                href={ticket.loomUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 font-medium text-violet-700 ring-1 ring-violet-200/70 hover:bg-violet-100"
              >
                <PlayCircle size={11} aria-hidden /> Your video
              </a>
            )}
          </div>
        </div>
        <StatusPill status={ticket.status} />
      </header>

      {ticket.status === "pending" && (
        <div className="flex items-center gap-2 border-b border-amber-200/70 bg-amber-50/80 px-5 py-2.5 text-[12.5px] text-amber-900">
          <Clock3 size={14} className="shrink-0 text-amber-600" aria-hidden />
          The team has replied — they're waiting on your answer below.
        </div>
      )}
      {ticket.status === "resolved" && (
        <div className="flex items-center gap-2 border-b border-emerald-200/70 bg-emerald-50/80 px-5 py-2.5 text-[12.5px] text-emerald-900">
          <CheckCircle2 size={14} className="shrink-0 text-emerald-600" aria-hidden />
          Marked as resolved. Still not right? Reply and it reopens.
        </div>
      )}

      <div className="relative min-h-0 flex-1 overflow-y-auto bg-ink-50/70">
        <div className="pointer-events-none absolute inset-0 bg-dotted bg-dot-md opacity-60" aria-hidden />
        <div className="relative space-y-5 px-5 py-6">
          {loading ? (
            <div className="space-y-5">
              <div className="flex justify-end">
                <div className="h-14 w-3/5 animate-pulse rounded-2xl bg-brand-100" />
              </div>
              <div className="flex items-end gap-3">
                <div className="h-8 w-8 animate-pulse rounded-full bg-ink-200/70" />
                <div className="h-12 w-1/2 animate-pulse rounded-2xl bg-white" />
              </div>
            </div>
          ) : (
            groups.map((g) =>
              g.kind === "day" ? (
                <div key={g.key} className="flex justify-center" role="separator">
                  <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-ink-500 shadow-sm ring-1 ring-ink-200/70">
                    {g.label}
                  </span>
                </div>
              ) : g.from === "customer" ? (
                <div key={g.key} className="flex flex-col items-end gap-1">
                  {g.messages.map((m, i) => (
                    <div
                      key={m._id}
                      className={cx(
                        "max-w-[78%] animate-fade-in whitespace-pre-line break-words rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 px-4 py-2.5 text-[14px] leading-relaxed text-white shadow-sm",
                        i < g.messages.length - 1 && "rounded-br-md",
                        i > 0 && "rounded-tr-md",
                        m.pending && "opacity-70",
                      )}
                    >
                      {m.body}
                    </div>
                  ))}
                  <span className="flex items-center gap-1 px-1 text-[11px] text-ink-400">
                    {g.messages[g.messages.length - 1].pending ? (
                      <>
                        <Clock3 size={11} /> Sending…
                      </>
                    ) : (
                      <>
                        You · {clock(g.messages[g.messages.length - 1].createdAt)}
                        {g === lastGroup &&
                          (ticket.seenBySupport ? (
                            <span className="ml-1 inline-flex items-center gap-0.5 font-medium text-brand-600">
                              <CheckCheck size={13} /> Seen
                            </span>
                          ) : (
                            <span className="ml-1 inline-flex items-center gap-0.5">
                              <Check size={13} /> Sent
                            </span>
                          ))}
                      </>
                    )}
                  </span>
                </div>
              ) : (
                <div key={g.key} className="flex items-end gap-3">
                  <TeamAvatar />
                  <div className="flex min-w-0 max-w-[78%] flex-col items-start gap-1">
                    <span className="px-1 text-[11.5px] font-semibold text-ink-700">
                      {g.authorName || "Support"} <span className="font-normal text-ink-400">· Support team</span>
                    </span>
                    {g.messages.map((m, i) => (
                      <div
                        key={m._id}
                        className={cx(
                          "max-w-full animate-fade-in whitespace-pre-line break-words rounded-2xl bg-white px-4 py-2.5 text-[14px] leading-relaxed text-ink-800 shadow-sm ring-1 ring-ink-200/70",
                          i < g.messages.length - 1 && "rounded-bl-md",
                          i > 0 && "rounded-tl-md",
                        )}
                      >
                        {m.body}
                      </div>
                    ))}
                    <span className="px-1 text-[11px] text-ink-400">{clock(g.messages[g.messages.length - 1].createdAt)}</span>
                  </div>
                </div>
              ),
            )
          )}
          <div ref={endRef} />
        </div>
      </div>

      <Composer onSend={onSend} closed={ticket.status === "closed"} />
    </div>
  );
};

/* ------------------------------------------------------------- new ticket */

const Hint = ({ id, length, min, max, checked }) => {
  const short = length < min;
  return (
    <div className="mt-1.5 flex items-start justify-between gap-3 text-[11.5px]">
      <p id={id} className={cx(short && checked ? "font-medium text-rose-600" : "text-ink-400")} role={short && checked ? "alert" : undefined}>
        {short && checked
          ? `Add at least ${min - length} more character${min - length === 1 ? "" : "s"} — the minimum is ${min}.`
          : `At least ${min} characters.`}
      </p>
      <span className={cx("shrink-0 tabular-nums", short ? "text-ink-400" : "text-emerald-600")}>
        {length}/{max}
      </span>
    </div>
  );
};

const NewTicket = ({ app, account, user, onCreated, onCancel }) => {
  const { support } = useAppServices();
  const toast = useToast();

  const [category, setCategory] = useState(CATEGORIES[0].id);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loom, setLoom] = useState("");
  const [checked, setChecked] = useState(false);
  const [sending, setSending] = useState(false);

  const subjectLen = subject.trim().length;
  const messageLen = message.trim().length;
  const loomBad = loom.trim().length > 0 && !LOOM.test(loom.trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (subjectLen < SUBJECT_MIN || messageLen < MESSAGE_MIN || loomBad) {
      setChecked(true);
      const first = subjectLen < SUBJECT_MIN ? "st-subject" : messageLen < MESSAGE_MIN ? "st-message" : "st-loom";
      document.getElementById(first)?.focus();
      return;
    }

    setSending(true);
    const { response, error } = await support.Create({
      ...account,
      category,
      subject,
      message,
      ...(loom.trim() && { loomUrl: loom.trim() }),
      submittedBy: user ? { userId: user.userId, userName: user.userName, email: user.email, role: user.role } : undefined,
    });
    setSending(false);

    if (error) {
      toast(error?.message || "Could not open the ticket", "error");
      return;
    }
    toast(`Ticket #${response.ticket.number} opened`);
    onCreated(response);
  };

  return (
    <form onSubmit={onSubmit} className="flex h-full min-h-0 flex-col">
      <header className="flex items-start gap-4 border-b border-ink-150 bg-brand-subtle px-6 py-5">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
          <LifeBuoy size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[16px] font-semibold tracking-tight text-ink-900">How can we help?</h3>
          <p className="mt-0.5 text-[13px] text-ink-600">
            Tell us what's going on with {app.appName}. The team that builds it replies right here.
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-500 hover:bg-white/70 hover:text-ink-800"
            aria-label="Close the new ticket form"
          >
            <X size={16} />
          </button>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto max-w-2xl space-y-6">
          <div>
            <Label>What's it about?</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5" role="radiogroup" aria-label="Ticket type">
              {CATEGORIES.map(({ id, label, icon: Icon }) => {
                const on = category === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setCategory(id)}
                    className={cx(
                      "flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-[12px] font-medium transition-all",
                      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                      on ? "border-brand-300 bg-brand-50 text-brand-700 shadow-glow" : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50",
                    )}
                  >
                    <Icon size={17} className={on ? "text-brand-600" : "text-ink-400"} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="st-subject" required>
              Subject
            </Label>
            <Input
              id="st-subject"
              className="mt-1.5"
              value={subject}
              maxLength={SUBJECT_MAX}
              invalid={checked && subjectLen < SUBJECT_MIN}
              aria-describedby="st-subject-hint"
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Replies aren't showing in conversations"
            />
            <Hint id="st-subject-hint" length={subjectLen} min={SUBJECT_MIN} max={SUBJECT_MAX} checked={checked} />
          </div>

          <div>
            <Label htmlFor="st-message" required>
              What's happening?
            </Label>
            <Textarea
              id="st-message"
              className="mt-1.5"
              rows={5}
              value={message}
              maxLength={MESSAGE_MAX}
              invalid={checked && messageLen < MESSAGE_MIN}
              aria-describedby="st-message-hint"
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What did you do, what did you expect, and what happened instead? Include any error message you saw."
            />
            <Hint id="st-message-hint" length={messageLen} min={MESSAGE_MIN} max={MESSAGE_MAX} checked={checked} />
          </div>

          <div className="rounded-xl border border-dashed border-ink-200 bg-ink-50/50 p-4">
            <Label htmlFor="st-loom" hint="optional">
              Add a screen recording
            </Label>
            <div className="relative mt-2">
              <Video size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-violet-500" aria-hidden />
              <Input
                id="st-loom"
                type="url"
                inputMode="url"
                className="bg-white pl-9"
                value={loom}
                maxLength={500}
                invalid={checked && loomBad}
                aria-describedby="st-loom-hint"
                onChange={(e) => setLoom(e.target.value)}
                placeholder="Paste a Loom link — https://www.loom.com/share/…"
              />
            </div>
            <p id="st-loom-hint" className={cx("mt-2 text-[11.5px]", checked && loomBad ? "font-medium text-rose-600" : "text-ink-500")} role={checked && loomBad ? "alert" : undefined}>
              {checked && loomBad ? (
                "That isn't a Loom link. In Loom, click Share › Copy link, then paste it here."
              ) : (
                <>
                  A short video shows us the problem faster than words.{" "}
                  <a href="https://www.loom.com/" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 hover:underline">
                    Record one free with Loom
                  </a>
                  .
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-ink-150 bg-white px-6 py-3.5">
        <p className="mr-auto hidden items-center gap-1.5 text-[11.5px] text-ink-400 sm:flex">
          <ShieldCheck size={12} aria-hidden /> Never include passwords or API keys — we never need them.
        </p>
        {onCancel && (
          <button type="button" onClick={onCancel} className="h-9 rounded-xl px-4 text-[13px] font-medium text-ink-600 hover:bg-ink-100">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={sending}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 px-4 text-[13px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
        >
          {sending ? "Opening…" : "Open ticket"} <SendHorizontal size={14} />
        </button>
      </footer>
    </form>
  );
};

/* ------------------------------------------------------------------ panel */

const SupportPanel = ({ app, appId, locationId, companyId, user }) => {
  const { support } = useAppServices();
  const toast = useToast();

  const account = useMemo(() => ({ appId, ...(locationId && { locationId }), ...(companyId && { companyId }) }), [appId, locationId, companyId]);
  const query = useMemo(() => new URLSearchParams(account).toString(), [account]);

  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [composing, setComposing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [live, setLive] = useState(false);

  // The stream handler reads the open ticket through a ref, so it doesn't have
  // to reconnect every time a different ticket is opened.
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  useEffect(() => {
    let alive = true;
    (async () => {
      const { response } = await support.List(query);
      if (!alive) return;
      const list = Array.isArray(response) ? response : [];
      setTickets(list);
      setLoadingList(false);
      if (list.length === 0) setComposing(true);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const open = useCallback(
    async (id) => {
      setComposing(false);
      setSelectedId(id);
      setMessages([]);
      setLoadingThread(true);
      const { response } = await support.Get(id, query);
      if (selectedRef.current !== id) return;
      setMessages(response?.messages || []);
      if (response?.ticket) setTickets((prev) => upsert(prev, response.ticket));
      setLoadingThread(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query],
  );

  // Live: replies from the team, status changes, and the "Seen" receipt.
  useEffect(() => {
    if (!env.API_URL || typeof EventSource === "undefined") return undefined;
    const source = new EventSource(`${env.API_URL}/support/stream?${query}`);

    source.addEventListener("ready", () => setLive(true));
    source.onerror = () => setLive(false); // the browser reconnects on its own

    source.addEventListener("ticket", (e) => {
      const { ticket } = JSON.parse(e.data);
      if (ticket) setTickets((prev) => upsert(prev, ticket));
    });

    source.addEventListener("message", (e) => {
      const { ticket, message } = JSON.parse(e.data);
      const isOpen = selectedRef.current === ticket?._id;
      if (ticket) setTickets((prev) => upsert(prev, isOpen ? { ...ticket, unread: 0 } : ticket));
      if (isOpen && message) {
        setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
        if (message.from === "admin") support.Read(ticket._id, account);
      }
    });

    return () => source.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const selected = tickets.find((t) => t._id === selectedId);

  // Shown at once with a "Sending…" state, swapped for the saved message when
  // the server confirms it; on failure it's removed and the text put back.
  const sendReply = async (body, restore) => {
    if (!selected) return;
    const temp = { _id: `tmp-${Date.now()}`, from: "customer", authorName: user?.userName, body, createdAt: new Date().toISOString(), pending: true };
    setMessages((prev) => [...prev, temp]);

    const { response, error } = await support.Reply(selected._id, { ...account, message: body, authorName: user?.userName });
    if (error) {
      setMessages((prev) => prev.filter((m) => m._id !== temp._id));
      restore();
      toast(error?.message || "Could not send the message", "error");
      return;
    }
    setTickets((prev) => upsert(prev, response.ticket));
    setMessages((prev) => {
      const without = prev.filter((m) => m._id !== temp._id);
      return without.some((m) => m._id === response.message._id) ? without : [...without, response.message];
    });
  };

  const startNew = () => {
    setSelectedId(null);
    setComposing(true);
  };

  const showingDetail = composing || Boolean(selected);
  const firstTicket = !loadingList && tickets.length === 0;
  const unreadTotal = tickets.reduce((n, t) => n + (t.unread || 0), 0);

  return (
    // The chat needs a fixed height (messages scroll above a composer that stays
    // put); the new-ticket form just takes the height of its content, so the
    // page scrolls instead of a box inside it.
    <div
      className={cx(
        "flex overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card",
        composing || firstTicket ? "min-h-[30rem]" : "h-[46rem]",
      )}
    >
      {/* Ticket list — hidden for someone who has never opened a ticket, so the
          form gets the whole panel instead of sitting beside an empty column. */}
      {!firstTicket && (
        <aside className={cx("w-full shrink-0 flex-col border-r border-ink-150 md:flex md:w-[19rem]", showingDetail ? "hidden" : "flex")}>
          <div className="border-b border-ink-150 px-4 pb-3.5 pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-baseline gap-2">
                <h3 className="text-[16px] font-semibold tracking-tight text-ink-900">Support</h3>
                {unreadTotal > 0 && (
                  <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 ring-1 ring-brand-200/70">
                    {unreadTotal} new
                  </span>
                )}
              </div>
              <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-ink-500" title={live ? "Replies appear instantly" : "Reconnecting…"}>
                <span className="relative flex h-2 w-2">
                  {live && <span className="absolute inline-flex h-full w-full animate-ping-slow rounded-full bg-emerald-400" />}
                  <span className={cx("relative inline-flex h-2 w-2 rounded-full", live ? "bg-emerald-500" : "bg-ink-300")} />
                </span>
                {live ? "Live" : "Connecting"}
              </span>
            </div>
            <button
              type="button"
              onClick={startNew}
              className="mt-3 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-[13px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            >
              <Plus size={15} /> New ticket
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="space-y-1 p-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-2 rounded-xl p-3">
                    <div className="h-3 w-2/3 animate-pulse rounded bg-ink-100" />
                    <div className="h-3 w-full animate-pulse rounded bg-ink-100" />
                  </div>
                ))}
              </div>
            ) : (
              <ul className="p-2">
                {tickets.map((t) => {
                  const active = t._id === selectedId;
                  const unread = t.unread > 0;
                  return (
                    <li key={t._id}>
                      <button
                        type="button"
                        onClick={() => open(t._id)}
                        className={cx(
                          "relative w-full rounded-xl px-3 py-3 text-left transition-colors",
                          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
                          active ? "bg-brand-50/70" : "hover:bg-ink-50",
                        )}
                      >
                        {active && <span className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-brand-600" />}
                        <span className="flex items-center gap-2">
                          <span className={cx("min-w-0 flex-1 truncate text-[13.5px]", unread ? "font-semibold text-ink-900" : "font-medium text-ink-800")}>
                            {t.subject}
                          </span>
                          <span className={cx("shrink-0 text-[11px]", unread ? "font-semibold text-brand-600" : "text-ink-400")}>{ago(t.lastMessageAt)}</span>
                        </span>
                        <span className="mt-1 flex items-center gap-2">
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink-500">
                            {t.lastMessageFrom === "customer" ? <span className="text-ink-400">You: </span> : <span className="font-medium text-ink-600">Support: </span>}
                            {t.lastMessagePreview}
                          </span>
                          {unread && (
                            <span className="grid h-5 min-w-[20px] shrink-0 place-items-center rounded-full bg-brand-600 px-1.5 text-[10.5px] font-semibold text-white">
                              {t.unread}
                            </span>
                          )}
                        </span>
                        <span className="mt-2 flex items-center gap-1.5">
                          <StatusPill status={t.status} short />
                          <span className="ml-auto font-mono text-[10.5px] text-ink-400">#{t.number}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      )}

      {/* Conversation, form, or a prompt to pick one */}
      <section className={cx("min-w-0 flex-1", !showingDetail && !firstTicket && "hidden md:block")}>
        {composing ? (
          <NewTicket
            app={app}
            account={account}
            user={user}
            onCancel={tickets.length > 0 ? () => setComposing(false) : undefined}
            onCreated={({ ticket, messages: first }) => {
              setTickets((prev) => upsert(prev, ticket));
              setComposing(false);
              setSelectedId(ticket._id);
              setMessages(first || []);
            }}
          />
        ) : selected ? (
          <Thread ticket={selected} messages={messages} loading={loadingThread} onSend={sendReply} onBack={() => setSelectedId(null)} />
        ) : (
          <div className="relative grid h-full place-items-center overflow-hidden bg-ink-50/70 p-8 text-center">
            <div className="pointer-events-none absolute inset-0 bg-dotted bg-dot-md opacity-60" aria-hidden />
            <div className="relative max-w-sm">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
                <MessagesSquare size={24} />
              </span>
              <h3 className="mt-5 text-[16px] font-semibold tracking-tight text-ink-900">Your conversations with support</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">
                Pick a ticket to see the conversation, or open a new one. Replies from the team appear here instantly.
              </p>
              <button
                type="button"
                onClick={startNew}
                className="mt-5 inline-flex h-9 items-center gap-1.5 rounded-xl border border-ink-200 bg-white px-4 text-[13px] font-medium text-ink-700 shadow-sm transition-colors hover:bg-ink-50"
              >
                <Plus size={14} /> New ticket
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SupportPanel;
